// The sheet under the model, in the Model tab's View Config (owner,
// 2026-10-09: "restore the sheet panel in construction tracking model view,
// and make sure we can select the areas sheet, with its areas colors added
// to it, then we can place it as we had the function do, scale and align it
// over each level"). It had been removed on 2026-10-02.
//
// Pick the sheet (or the Areas tab's sheet straight away), its page, show
// it with the zones painted on, align it by two points - and the number
// boxes and sliders to NUDGE it after (owner, 2026-10-09: "what you left
// out could help align the sheet"). The boxes and the two-point align edit
// ONE state, the page's form.sheet: the align writes scale / turn / X / Z
// into it and the boxes show and edit those same values, so a nudge after
// an align is saved with it and nothing overrides anything on reload.
//
// Its own component, not more JSX inside ModelStage: that component is big
// enough that every extra branch in it trips the rules-of-hooks path limit
// (a false "hook called conditionally" that fails the build).
import React from "react";
import { Button, InputNumber, Select, Slider, Space, Switch, Tooltip } from "antd";
import { UndoOutlined } from "@ant-design/icons";
import { popupInStage } from "./stagePopup";
import { sheetSelectProps } from "./sheetOptions";
import { sheetLabel } from "./trackingTheme";
import { areaColor } from "./areaSheetPaint";
import TwoPointAlign from "./TwoPointAlign";
import { useAreaSystems, AreaSystemsHint } from "./areaSystemsHint";
import { SHEET_GRID_WHY } from "api/glance/sheetGrid";

const T = { line: "#E5E7EB", muted: "#6B7280", ink: "#111827" };
const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => Math.round((r * 180) / Math.PI);

export default function SheetUnderModel({
  projectId, sheets = [], sheetId, sheet, sheetPage = 1, onSheetChange, canEdit = false, sheetFollowsLevel = false,
  areaPages = { pages: [], unplaced: 0 }, pageAreas = [],
  sheetOn = false, onShowSheet, showAreas = true, onShowAreas,
  sheetGridOn = false, onSheetGrid = null, sheetGrid = null,
  form = null, onSet, drawImg = "", viewer,
  canFit = false, dirty = false, onApplyAlign, onFit, onUndo,
}) {
  const numberRow = (label, value, onChange, step = 1) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <span style={{ width: 74, fontSize: 12, color: T.muted }}>{label}</span>
      <InputNumber size="small" value={value} step={step} onChange={onChange} style={{ width: 110 }} disabled={!canEdit} />
    </div>
  );
  const slider = (label, value, onChange, props) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <span style={{ width: 74, fontSize: 12, color: T.muted }}>{label}</span>
      <Slider style={{ flex: 1 }} value={value} disabled={!canEdit} onChange={onChange} {...props} />
    </div>
  );
  // Each area's tagged elements and the systems they point to, read only
  // while the areas are listed.
  const systemsById = useAreaSystems(projectId, !!(sheetOn && showAreas && pageAreas.length > 0));
  const sh = (form && form.sheet) || {};
  const md = (form && form.model) || {};
  const set = (part, patch) => onSet && onSet(part, patch);
  return (
    <>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 6 }}>Sheet under the model</div>
      {sheetFollowsLevel && (
        <div style={{ fontSize: 11, color: T.muted, marginBottom: 6, lineHeight: 1.4 }}>
          This page follows its level: the drawing is the one set on Levels, Areas &amp; Systems. Change it there, or pick "Its own drawing" on the page bar.
        </div>
      )}
      {areaPages.pages.length > 0 && (
        <Select getPopupContainer={popupInStage}
          size="small"
          style={{ width: "100%", marginBottom: 6 }}
          placeholder="Levels & Areas sheet"
          value={pageAreas.length ? `${sheetId}|${sheetPage}` : undefined}
          disabled={!canEdit || !onSheetChange}
          onChange={(k) => {
            const [id, pg] = String(k).split("|");
            if (onSheetChange) onSheetChange(id, Number(pg) || 1);
            onShowSheet(true);
            onShowAreas(true);
          }}
          options={areaPages.pages.map((r) => {
            const s = sheets.find((x) => String(x._id) === r.sheetId);
            const levels = r.levels.length ? r.levels.join(", ") : "no level";
            return {
              value: `${r.sheetId}|${r.page}`,
              label: `Areas · ${levels} · ${r.count} area${r.count === 1 ? "" : "s"}${s && (s.pages || []).length > 1 ? ` · page ${r.page}` : ""}`,
              title: s ? sheetLabel(s) : "",
            };
          })}
        />
      )}
      {areaPages.unplaced > 0 && (
        <div style={{ fontSize: 11, color: T.muted, marginBottom: 6, lineHeight: 1.4 }}>
          {areaPages.unplaced} area{areaPages.unplaced === 1 ? " is" : "s are"} drawn on an image that is not a Document Control sheet, so {areaPages.unplaced === 1 ? "it" : "they"} cannot be placed here.
        </div>
      )}
      <Select getPopupContainer={popupInStage}
        size="small"
        style={{ width: "100%", marginBottom: 6 }}
        placeholder="Pick a sheet"
        value={sheetId || undefined}
        disabled={!canEdit || !onSheetChange}
        onChange={(id) => onSheetChange && onSheetChange(id, 1)}
        {...sheetSelectProps(sheets)}
        notFoundContent={
          <span style={{ fontSize: 12 }}>
            No sheets on this project yet. Upload drawings in Document Control and they appear here.
          </span>
        }
      />
      {sheet && (sheet.pages || []).filter(Boolean).length > 1 && (
        <Select getPopupContainer={popupInStage}
          size="small"
          style={{ width: "100%", marginBottom: 6 }}
          value={sheetPage}
          disabled={!canEdit || !onSheetChange}
          onChange={(pg) => onSheetChange && onSheetChange(sheetId, pg)}
          options={(sheet.pages || []).filter(Boolean).map((_, i) => ({ value: i + 1, label: `Page ${i + 1}` }))}
        />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0 6px" }}>
        <Switch size="small" checked={sheetOn} disabled={!sheetId} onChange={(on) => onShowSheet(!!on)} />
        <span style={{ fontSize: 12, color: sheetId ? T.ink : T.muted }}>
          {sheetId ? "Show the sheet" : "No sheet on this page"}
        </span>
      </div>
      {sheetId && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Switch size="small" checked={showAreas} disabled={!pageAreas.length} onChange={(on) => onShowAreas(!!on)} />
          <span style={{ fontSize: 12, color: pageAreas.length ? T.ink : T.muted }}>
            {pageAreas.length ? `Show the areas · ${pageAreas.length}` : "No areas drawn on this sheet page"}
          </span>
        </div>
      )}
      {sheetId && onSheetGrid && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Switch size="small" aria-label="Building grid on the sheet" checked={sheetGridOn} disabled={!sheetOn} onChange={(on) => onSheetGrid(!!on)} />
          <span data-testid="sheet-grid-state" style={{ fontSize: 12, color: sheetOn ? T.ink : T.muted }}>
            {sheetGridOn && sheetGrid && !sheetGrid.loading && !sheetGrid.lines.length
              ? (SHEET_GRID_WHY[sheetGrid.reason] || SHEET_GRID_WHY.unavailable)
              : sheetGridOn && sheetGrid && sheetGrid.lines.length
                ? `Building grid · ${sheetGrid.lines.length} lines`
                : "Building grid"}
          </span>
        </div>
      )}
      {sheetOn && showAreas && pageAreas.length > 0 && (
        <div style={{ maxHeight: 200, overflowY: "auto", border: `1px solid ${T.line}`, borderRadius: 4, padding: "4px 6px", marginBottom: 6 }}>
          {pageAreas.map((a) => (
            <div key={String(a._id)}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, lineHeight: 1.6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: areaColor(a), flexShrink: 0 }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.name}>{a.name}</span>
              </div>
              {systemsById && <AreaSystemsHint projectId={projectId} areaId={String(a._id)} row={systemsById[String(a._id)]} />}
            </div>
          ))}
        </div>
      )}
      {sheetId && form && (
        <>
          {numberRow("Width", sh.scale, (v) => set("sheet", { scale: v }), 5)}
          {numberRow("X", sh.position?.x, (v) => set("sheet", { position: { ...(sh.position || {}), x: v } }))}
          {numberRow("Height", sh.position?.y, (v) => set("sheet", { position: { ...(sh.position || {}), y: v } }))}
          {numberRow("Z", sh.position?.z, (v) => set("sheet", { position: { ...(sh.position || {}), z: v } }))}
          {slider("Turn", sh.rotation, (v) => set("sheet", { rotation: v }), { min: -180, max: 180 })}
          {slider("Fade", sh.opacity, (v) => set("sheet", { opacity: v }), { min: 0, max: 1, step: 0.05 })}
        </>
      )}
      {sheetId && (
        <Space wrap size={6} style={{ marginBottom: 8 }}>
          <TwoPointAlign
            sheetImg={drawImg}
            onNeedSheet={() => onShowSheet(true)}
            viewer={viewer}
            disabled={!canEdit || !sheetId}
            onTopView={() => { const v = viewer(); try { if (v && v.setStandardView) v.setStandardView("top"); } catch (e) { /* best effort */ } }}
            onApply={onApplyAlign}
          />
          <Tooltip getPopupContainer={popupInStage} title="The model on the left, the sheet on the right, two points picked on each - saves for every page that uses this sheet page">
            <Button size="small"
              onClick={() => { window.location.href = `/align/${projectId}?docId=${sheetId}&page=${sheetPage}`; }}>
              Align page
            </Button>
          </Tooltip>
          <Tooltip getPopupContainer={popupInStage} title="Put the sheet back under the model, sized to it">
            <Button size="small" disabled={!canFit} onClick={onFit}>Fit sheet</Button>
          </Tooltip>
          <Tooltip getPopupContainer={popupInStage} title="Back to the saved alignment">
            <Button size="small" icon={<UndoOutlined />} disabled={!dirty} onClick={onUndo} />
          </Tooltip>
        </Space>
      )}
      {sheetId && form && (
        <>
          {/* MODEL OVER IT: where the MODEL sits over the drawing (its own
              move / turn / scale, saved with the page) - for a drawing that
              is right and a model that is not where it says it is. */}
          <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, margin: "10px 0 6px" }}>Model over it</div>
          {numberRow("X", md.position?.x, (v) => set("model", { position: { ...(md.position || {}), x: v } }))}
          {numberRow("Y", md.position?.y, (v) => set("model", { position: { ...(md.position || {}), y: v } }))}
          {numberRow("Z", md.position?.z, (v) => set("model", { position: { ...(md.position || {}), z: v } }))}
          {slider("Turn", deg(md.rotation?.y || 0), (v) => set("model", { rotation: { ...(md.rotation || {}), y: rad(v) } }), { min: -180, max: 180 })}
          {numberRow("Scale", md.scale, (v) => set("model", { scale: v }), 0.05)}
        </>
      )}
    </>
  );
}
